import { useLayoutEffect, useRef, useState } from 'react';
import { visibleTableRange } from '../tier0/kit-table.mjs';

// Density and coarse-pointer row sizes come from DES1's CSS, never JS constants.
export function useTableWindow(rowCount: number, density: string) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState({ rowHeight: 0, viewportHeight: 0, scrollTop: 0 });
  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    const measure = () => {
      const styles = getComputedStyle(scroll);
      const rowHeight = Number.parseFloat(styles.getPropertyValue?.('--pc-dt-row-h') ?? '');
      const headHeight = headRef.current?.getBoundingClientRect().height ?? 0;
      if (!Number.isFinite(rowHeight) || rowHeight <= 0) return;
      setMetrics({
        rowHeight,
        viewportHeight: Math.max(0, (scroll.clientHeight || 0) - headHeight),
        scrollTop: Math.max(0, (scroll.scrollTop || 0) - headHeight),
      });
    };
    measure();
    scroll.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const coarse = window.matchMedia('(pointer: coarse)');
    coarse.addEventListener('change', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(scroll);
    return () => {
      observer?.disconnect();
      scroll.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      coarse.removeEventListener('change', measure);
    };
  }, [rowCount, density]);
  const range =
    metrics.rowHeight > 0
      ? visibleTableRange({ rowCount, ...metrics })
      : { start: 0, end: 0, offsetTop: 0, totalHeight: 0 };
  return { scrollRef, headRef, range, rowHeight: metrics.rowHeight };
}

export function usePhoneTable() {
  const [phone, setPhone] = useState(false);
  useLayoutEffect(() => {
    const media = window.matchMedia('(max-width: 640px)');
    const update = () => setPhone(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return phone;
}
